import { ReactNode } from "react";
import { ChangePeriod, changePeriodToDisplayName } from "./ChangePeriod";
import { PercentageChangeCell } from "./components/cells/PercentageChangeCell";
import { SimpleTextCell } from "./components/cells/SimpleTextCell";
import { TokenTableEntry } from "./TokenTableEntry";

// Example: "en-US"
// undefined - use user's locale
const locale = undefined;

export enum Column {
  Index = "INDEX",
  Name = "NAME",
  Price = "PRICE",
  PriceChange = "PRICE_CHANGE",
  MarketCap = "MARKET_CAP",
  Volume = "VOLUME",
  VolumeChange = "VOLUME_CHANGE",
  GatewayScore = "GATEWAY_SCORE",
  GatewayScoreChange = "GATEWAY_SCORE_CHANGE",
}

export enum ColumnWidth {
  Tiny = "TINY",
  Short = "SHORT",
  Medium = "MEDIUM",
  Wide = "WIDE",
  Full = "FULL",
}

export const columnDef: Record<
  Column,
  {
    width: ColumnWidth;
    displayHeader: (period: ChangePeriod) => string;
    displayData: (data: {
      index: number;
      entry: TokenTableEntry;
      period: ChangePeriod;
    }) => ReactNode;
  } & (
    | { orderByEnabled: false }
    | {
        orderByEnabled: true;
        orderByToDataField: (data: {
          entry: TokenTableEntry;
          period: ChangePeriod;
        }) => string | number;
      }
  )
> = {
  [Column.Index]: {
    width: ColumnWidth.Tiny,
    orderByEnabled: false,
    displayHeader: () => "#",
    displayData: ({ index }) => (
      <div className="font-medium text-sm text-white">
        {index.toString().padStart(2, "0")}
      </div>
    ),
  },
  [Column.Name]: {
    width: ColumnWidth.Full,
    orderByEnabled: true,
    displayHeader: () => "Token",
    displayData: ({ entry }) => (
      <div className="flex items-center">
        {entry.logoSrc ? (
          <img
            src={entry.logoSrc}
            alt={`${entry.name} logo`}
            className="w-[38px] min-w[38px] max-w-[38px] h-[38px] min-h-[38px] max-h-[38px] bg-white rounded-full mr-3"
          />
        ) : (
          <div className="w-[38px] min-w[38px] max-w-[38px] h-[38px] min-h-[38px] max-h-[38px] bg-[#8b8b8b] rounded-full mr-3"></div>
        )}
        <div className="font-medium text-sm text-white whitespace-nowrap overflow-hidden text-ellipsis mr-1.5">
          {entry.name}
        </div>
        <div className="font-medium text-sm text-[rgba(255,255,255,0.5)] whitespace-nowrap">
          {entry.symbol}
        </div>
      </div>
    ),
    orderByToDataField: ({ entry }) => entry.name,
  },
  [Column.Price]: {
    width: ColumnWidth.Medium,
    orderByEnabled: true,
    displayHeader: () => "Price",
    displayData: ({ entry }) => (
      <SimpleTextCell
        text={`${entry.priceUSD.current.toLocaleString(locale, {
          maximumFractionDigits: 4,
        })} USD`}
      />
    ),
    orderByToDataField: ({ entry }) => entry.priceUSD.current,
  },
  [Column.PriceChange]: {
    width: ColumnWidth.Short,
    orderByEnabled: true,
    displayHeader: (period) => `Δ${changePeriodToDisplayName[period]}`,
    displayData: ({ entry, period }) => (
      <PercentageChangeCell
        change={entry.priceUSD.changeRatio[period]}
        locale={locale}
      />
    ),
    orderByToDataField: ({ entry, period }) =>
      entry.priceUSD.changeRatio[period],
  },
  [Column.MarketCap]: {
    width: ColumnWidth.Medium,
    orderByEnabled: true,
    displayHeader: () => "Market Cap",
    displayData: ({ entry }) => (
      <SimpleTextCell
        text={`${entry.marketCapUSD.current.toLocaleString(locale, {
          maximumFractionDigits: 0,
        })} USD`}
      />
    ),
    orderByToDataField: ({ entry }) => entry.marketCapUSD.current,
  },
  [Column.Volume]: {
    width: ColumnWidth.Medium,
    orderByEnabled: true,
    displayHeader: () => `Volume (24h)`,
    displayData: ({ entry }) => (
      <SimpleTextCell
        text={`${entry.volumeUSD.range["24H"].toLocaleString(locale, {
          maximumFractionDigits: 0,
        })} USD`}
      />
    ),
    orderByToDataField: ({ entry, period }) => entry.volumeUSD.range[period],
  },
  [Column.VolumeChange]: {
    width: ColumnWidth.Short,
    orderByEnabled: true,
    displayHeader: (period) => `Δ${changePeriodToDisplayName[period]}`,
    displayData: ({ entry, period }) => (
      <PercentageChangeCell
        change={entry.volumeUSD.changeRatio[period]}
        locale={locale}
      />
    ),
    orderByToDataField: ({ entry, period }) =>
      entry.volumeUSD.changeRatio[period],
  },
  [Column.GatewayScore]: {
    width: ColumnWidth.Medium,
    orderByEnabled: true,
    displayHeader: () => "Gateway Score",
    displayData: ({ entry }) => (
      <SimpleTextCell
        text={entry.gatewayScore.current.toLocaleString(locale, {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        })}
      />
    ),
    orderByToDataField: ({ entry }) => entry.gatewayScore.current,
  },
  [Column.GatewayScoreChange]: {
    width: ColumnWidth.Short,
    orderByEnabled: true,
    displayHeader: (period) => `Δ${changePeriodToDisplayName[period]}`,
    displayData: ({ entry, period }) => (
      <PercentageChangeCell
        change={entry.gatewayScore.changeRatio[period]}
        locale={locale}
      />
    ),
    orderByToDataField: ({ entry, period }) =>
      entry.gatewayScore.changeRatio[period],
  },
};
